import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmPopupComponent } from 'src/app/modules/confirm-popup/confirm-popup.component';
import { PortRequestFormServiceService } from '../../service/port-request-FormService/port-request-form.service.service';
import { ReportVerifyPopupComponent } from '../report-verify-popup/report-verify-popup.component';
import { ToastrService } from 'ngx-toastr';

interface EventReport {
   id: number; 
  eventsList: string;
  report_timestamp: string;
  submitted: boolean;
  verified: boolean;
  force_submission?: boolean;
}

@Component({
  selector: 'app-retrive-reports-fetch',
  templateUrl: './retrive-reports-fetch.component.html',
  styleUrls: ['./retrive-reports-fetch.component.scss'],
  providers: [DatePipe]
})
export class RetriveReportsFetchComponent implements OnInit {

  vesselList: { vesselName: string; vesselIMO: string }[] = [];
  dateList: { label: string; value: string }[] = [];
  eventList: EventReport[] = [];

  selectedVesselIMO: string = '';
  selectedDate: string = '';
  selectedEvent: EventReport | null = null;

 isSubmitting = false;
  actionInProgressId: number | null = null;

  overwriteTarget: EventReport | null = null;
  overwriteFile: File | null = null;
  overwritePreview: { eventsList: string; time: string } | null = null;
  overwriteError = '';
  constructor(
    private portRequestFormService: PortRequestFormServiceService,
    private dialogRef: MatDialogRef<ReportVerifyPopupComponent>,
    private dialog: MatDialog,
    private router: Router,
     private toastr: ToastrService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.loadVessels();
  }

  //  Load vessels
  loadVessels(): void {
    this.portRequestFormService.getVessels().subscribe({
      next: (res: any) => this.vesselList = res.vessels || [],
      error: err => console.error('Vessel Load Error:', err)
    });
  }

  //  Vessel selected → Load report dates
  onVesselChange(): void {
    this.selectedDate = '';
    this.selectedEvent = null;
    this.eventList = [];
    this.cancelOverwrite();
    this.dateList = [];

    if (!this.selectedVesselIMO) return;

    this.portRequestFormService
      .getReportsByVessel(this.selectedVesselIMO)
      .subscribe({
        next: (res: any) => {
          if (!res?.reports?.length) return;

          this.dateList = res.reports.map((dateStr: string) => {
            const d = new Date(dateStr);
            return {
              label: d.toDateString(),
              value: d.toISOString().split('T')[0]
            };
          });
        },
        error: err => console.error('Date Load Error:', err)
      });
  }

  // Date selected → Load reports (events + status)
 onDateChange(): void {
  this.selectedEvent = null;
  this.eventList = [];
  this.cancelOverwrite();

  if (!this.selectedVesselIMO || !this.selectedDate) return;

  this.portRequestFormService
    .getReportsByVesselAndDate(this.selectedVesselIMO, this.selectedDate)
    .subscribe({
      next: (res: any) => {
        this.eventList = res.report || [];  // includes id, submitted, verified
      },
      error: err => console.error('Event Load Error:', err)
    });
}


fetchReport(): void {
  if (!this.selectedEvent) return;

  this.isSubmitting = true;

  const imo = this.selectedVesselIMO;
  const reportId = this.selectedEvent.id;

  this.portRequestFormService.getPendingReports(imo, reportId)
    .subscribe({
      next: (data: any) => {
        this.isSubmitting = false;

        //  Backend response but no pending report
        if (data?.message) {
          this.toastr.warning(data.message, 'No Pending Report');
          return;
        }

        //  Success
        this.toastr.success('Pending report loaded successfully!', 'Success');
        this.portRequestFormService.setReportData(data);
        this.dialogRef.close();
        this.router.navigate(['/pc-newForm']);
      },

      error: (err) => {
        this.isSubmitting = false;

        //  Backend sent message with 4xx/404
        if (err?.error?.message) {
          this.toastr.warning(err.error.message, 'No Pending Report');
          return;
        }

        // Real server/network issue
        console.error(err);
        this.toastr.error(
          'Unable to reach server. Please try again.',
          'Server Error'
        );
      }
    });
}





// OPEN: mark json_report unverified and delete its master_reports row
openEvent(event: EventReport): void {
  const label = this.eventLabel(event);
  if (!confirm(`OPEN ${label}?\n\nThis marks the report as Not Verified and deletes its master report record.`)) return;

  this.runEventAction(
    event,
    this.portRequestFormService.openJsonReport({
      json_report_id: event.id,
      vesselIMO: this.selectedVesselIMO,
    }),
    'Report reopened',
  );
}

// OVERWRITE: upload a new vessel JSON (any event / time) that replaces this report as its next version
startOverwrite(event: EventReport): void {
  this.cancelOverwrite();
  this.overwriteTarget = event;
}

cancelOverwrite(): void {
  this.overwriteTarget = null;
  this.overwriteFile = null;
  this.overwritePreview = null;
  this.overwriteError = '';
}

onOverwriteFileSelected(input: HTMLInputElement): void {
  const file = input.files?.[0];
  input.value = '';
  this.overwriteFile = null;
  this.overwritePreview = null;
  this.overwriteError = '';
  if (!file) return;

  file.text().then((text) => {
    let json: any;
    try {
      json = JSON.parse(text.replace(/^﻿/, ''));
    } catch {
      this.overwriteError = 'File is not valid JSON.';
      return;
    }
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      this.overwriteError = 'JSON must be a single report object.';
      return;
    }
    if (String(json.vesselIMO ?? '').trim() !== String(this.selectedVesselIMO)) {
      this.overwriteError = `File is for IMO ${json.vesselIMO ?? '—'}, not ${this.selectedVesselIMO}.`;
      return;
    }

    const pad = (v: any) => String(Number(v) || 0).padStart(2, '0');
    this.overwriteFile = file;
    this.overwritePreview = {
      eventsList: json.eventsList || '—',
      time: `${json.reportDate || '—'} ${pad(json.reportHour)}:${pad(json.reportMinute)}`,
    };
  });
}

confirmOverwrite(): void {
  const event = this.overwriteTarget;
  if (!event || !this.overwriteFile || !this.overwritePreview) return;

  const label = this.eventLabel(event);
  const next = `${this.overwritePreview.eventsList} — ${this.overwritePreview.time} UTC`;
  if (!confirm(`OVERWRITE ${label}\nwith ${next}?\n\nThe master report record is deleted, the current report is marked Not Verified / inactive, and the uploaded file is saved as its next version.`)) return;

  this.runEventAction(
    event,
    this.portRequestFormService.overwriteJsonReport(this.overwriteFile, {
      json_report_id: event.id,
      vesselIMO: this.selectedVesselIMO,
    }),
    'Report overwritten',
  );
}

private runEventAction(event: EventReport, request$: Observable<any>, successTitle: string): void {
  this.actionInProgressId = event.id;

  request$.subscribe({
    next: (res: any) => {
      this.actionInProgressId = null;
      this.toastr.success(res?.message || successTitle, successTitle);
      this.onDateChange();
    },
    error: (err) => {
      this.actionInProgressId = null;
      console.error(err);
      this.toastr.error(err?.error?.error || 'Unable to reach server. Please try again.', 'Error');
    },
  });
}

private eventLabel(event: EventReport): string {
  const time = this.datePipe.transform(event.report_timestamp, 'dd MMM yyyy, HH:mm', 'UTC');
  return `${event.eventsList} — ${time} UTC`;
}

deleteEvent(event: EventReport): void {
  const label = this.eventLabel(event);
  this.actionInProgressId = event.id;

  this.portRequestFormService.checkCanDeleteJsonReport({
    json_report_id: event.id,
    vesselIMO: this.selectedVesselIMO,
  }).subscribe({
    next: (res: any) => {
      this.actionInProgressId = null;

      if (!res.can_delete) {
        // Linked with Report Desk or Auto Checks -> Cannot delete, offer overwrite
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
          width: '460px',
          disableClose: true,
          panelClass: 'custom-dialog-container',
          data: {
            mainTitle: 'Cannot Delete Report',
            title: `This report has linked records (${res.email_count || 0} email(s), ${res.check_count || 0} auto-check(s)). It cannot be deleted.\n\nWould you like to Overwrite this report with a new JSON file instead?`,
            yesText: 'Overwrite Now',
            noText: 'Cancel',
            iconClass: 'fa-solid fa-triangle-exclamation',
            isDanger: false,
          }
        });

        dialogRef.afterClosed().subscribe((proceedOverwrite) => {
          if (proceedOverwrite) {
            this.startOverwrite(event);
          }
        });
        return;
      }

      // Eligible for delete -> confirm with user
      const dialogRef = this.dialog.open(ConfirmPopupComponent, {
        width: '460px',
        disableClose: true,
        panelClass: 'custom-dialog-container',
        data: {
          mainTitle: 'Confirm Delete',
          title: `Are you sure you want to permanently delete ${label}?\n(This will delete the JSON report and its master records).`,
          yesText: 'Yes, Delete',
          noText: 'Cancel',
          iconClass: 'fa-solid fa-trash-can',
          isDanger: true,
        }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (!confirmed) return;

        this.runEventAction(
          event,
          this.portRequestFormService.deleteJsonReport({
            json_report_id: event.id,
            vesselIMO: this.selectedVesselIMO,
          }),
          'Report deleted'
        );
      });
    },
    error: (err) => {
      this.actionInProgressId = null;
      console.error(err);
      this.toastr.error(err?.error?.error || 'Failed to verify delete eligibility.', 'Error');
    }
  });
}

  closeModal(): void {
    this.dialogRef.close();
  }
}
